import { FormQuestionsObject, TemplateNode, FormObject, FormQuestion } from '@/components/DynamicForms/DynamicFormsTypes'
import { ref, Ref, computed, ComputedRef } from '@vue/composition-api'
import { useLoading } from '@coursedog/composables'
import store from '@/store'
import { User } from '@shared/users/types'
import {
  getCourseTemplate,
  putCourseTemplate,
  getProgramTemplate,
  putProgramTemplate,
  getSectionTemplateV2,
  putSectionTemplateV2,
  getTermTemplate
} from '@/api'

import type { GeneralAttributeResponse } from '@shared/general/types'

export interface FormsCache {
  [resource: string]: FormObject
}

const baseFormsCache: Ref<FormsCache> = ref({})

export function getQuestionsFlat (
  questions: FormQuestionsObject = {},
  path: string[] = []
): FormQuestionsObject {
  const keys = Object.keys(questions)
  if (!keys.length) return {}
  return keys.reduce((acc, questionId) => {
    const fields = questions[questionId].fields || questions[questionId].config?.fields || {}
    const children = getQuestionsFlat(fields, [ ...path, questionId ])
    const questionIdWithPath = [ ...path, questionId ].join('.')
    const question = {
      ...questions[questionId],
      path,
    }
    return { ...acc, [questionIdWithPath]: question, ...children }
  }, {})
}

interface DocumentTemplateOptions {
  readonly type: Ref<string>,
  readonly isSystemType: Ref<boolean>,
  readonly school: string
}

export default function useDocumentTemplate ({
  type,
  isSystemType = ref(false),
  school = store.getters.school,
}: DocumentTemplateOptions) {
  const user: User = store.state.user as User
  const { isLoading, startLoading, stopLoading } = useLoading()

  const baseForm: ComputedRef<FormObject> = computed(() => baseFormsCache.value[type?.value])
  const baseQuestions: ComputedRef<FormQuestionsObject> = computed(() => baseForm.value
    ? baseForm.value.questions
    : {}
  )
  const baseQuestionsArray: ComputedRef<FormQuestion[]> = computed(() => Object.values(baseQuestions.value))
  const baseTemplate: ComputedRef<TemplateNode[]> = computed(() => baseForm.value
    ? baseForm.value.template
    : []
  )

  const baseQuestionsFlat = computed(() => getQuestionsFlat(baseQuestions.value))

  async function loadBaseTemplate ({ useCache = false } = {}): Promise<void> {
    if (!type || !type.value) return
    if (useCache && Object.keys(baseForm.value || {}).length) return
    startLoading('baseTemplate')

    const data = await fetchBaseTemplateByResource(type.value)

    if (data) {
      // TODO: Hack to ensure section template is saved under the correct attribute name
      const attribute = data.attribute === 'sectionTemplate2' ? 'sectionTemplate' : data.attribute

      baseFormsCache.value = {
        ...baseFormsCache.value,
        [type.value]: data?.[attribute],
      }
    }

    stopLoading('baseTemplate')
  }

  async function fetchBaseTemplateByResource (resource: string): Promise<GeneralAttributeResponse<FormObject> | undefined> {
    switch (resource) {
      case 'course':
        return getCourseTemplate({ school })
      case 'program':
        return getProgramTemplate({ school })
      case 'section':
        return getSectionTemplateV2({ school })
      case 'termTemplate':
        return getTermTemplate({ school })
      default:
        if (isSystemType.value) return
        const response = await store.dispatch('documentTypes/fetchById', { id: resource }) as FormObject
        return { attribute: resource, [resource]: response } as GeneralAttributeResponse<FormObject>
    }
  }

  async function updateBaseTemplate (newBaseForm: FormObject) {
    const updateMethod = getUpdateMethodForType(type.value)

    await updateMethod({
      school,
      ...newBaseForm,
      lastEditedBy: user.email,
      lastEditedAt: Date.now(),
    })
  }

  return {
    baseForm,
    baseQuestions,
    baseQuestionsFlat,
    baseQuestionsArray,
    baseTemplate,
    loadBaseTemplate,
    updateBaseTemplate,
    isLoading,
  }
}

function getUpdateMethodForType (type: string) {
  switch (type) {
    case 'course':
      return async ({ school, ...courseTemplate }: { school: string, lastEditedAt: number, lastEditedBy: string }): Promise<void> => {
        store.dispatch('setCurrentPut', 'putCourseTemplate')

        await putCourseTemplate({
          school,
          courseTemplate,
          _id: 'courseTemplate',
          attribute: 'courseTemplate',
        })

        store.dispatch('unsetCurrentPut', 'putCourseTemplate')
      }
    case 'program':
      return async ({ school, ...programTemplate }: { school: string, lastEditedAt: number, lastEditedBy: string }): Promise<void> => {
        store.dispatch('setCurrentPut', 'putProgramTemplate')

        await putProgramTemplate({
          school,
          programTemplate,
          _id: 'programTemplate',
          attribute: 'programTemplate',
        })

        store.dispatch('unsetCurrentPut', 'putProgramTemplate')
      }
    case 'section':
      return async ({ school, ...sectionTemplate }: { school: string, lastEditedAt: number, lastEditedBy: string }): Promise<void> => {
        store.dispatch('setCurrentPut', 'putSectionTemplate')

        await putSectionTemplateV2({
          school,
          sectionTemplate,
        })

        // TODO: Uncomment once decided to update store’s version of sectionTemplate
        // store.commit('sectionTemplate/SET_SECTION_TEMPLATE', sectionTemplate)
        store.dispatch('unsetCurrentPut', 'putSectionTemplate')
      }
    default:
      throw new Error('Invalid Base Template Type')
  }
}
